import type { ElementType } from "react";
import { IconX, IconMenu2 } from "@tabler/icons-react";
import { LibraryBig } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebarContext } from "@/contexts/SidebarContext";

export interface Tab {
  id: string;
  title: string;
  icon?: ElementType;
  content: 'home' | 'custom' | 'youtube' | 'pdf' | 'slide-deck';
  data?: {
    videoUrl?: string;
    videoId?: string;
    journalId?: string; // For fetching transcript from DB
    pdfUrl?: string;
    fileName?: string;
    slides?: any[]; // SlideContent[];
    deckTitle?: string;
  };
}

interface SpaceTabBarProps {
  tabs: Tab[];
  activeTabId: string;
  dragOverTabId: string | null;
  journalName: string;
  onTabClick: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
  onTabDragStart: (e: React.DragEvent<HTMLDivElement>, tabId: string) => void;
  onTabDragOver: (e: React.DragEvent<HTMLDivElement>, tabId: string) => void;
  onTabDragLeave: () => void;
  onTabDrop: (e: React.DragEvent<HTMLDivElement>, tabId: string) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

export const SpaceTabBar = ({
  tabs,
  activeTabId,
  dragOverTabId,
  journalName,
  onTabClick,
  onTabClose,
  onTabDragStart,
  onTabDragOver,
  onTabDragLeave,
  onTabDrop,
  isFullscreen = false,
  onToggleFullscreen,
}: SpaceTabBarProps) => {
  const isMobile = useIsMobile();
  const { toggleSidebar } = useSidebarContext();

  return (
    <div className="flex-shrink-0 sticky top-0 z-10 bg-[#F8F8F6]">
      <div className="flex items-center">
        {/* Mobile Hamburger Menu */}
        {isMobile && (
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center p-2 ml-1 text-muted-foreground hover:text-foreground hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Open sidebar"
          >
            <IconMenu2 className="w-5 h-5" />
          </button>
        )}

        {/* Tab Bar - scrollable on mobile, flex on desktop */}
        <div className="flex bg-[#f0f0ef] flex-1 overflow-x-auto md:overflow-x-visible scrollbar-hide">
          {tabs.map(tab => {
            const isHome = tab.id === 'home';
            const displayTitle = isHome ? journalName : tab.title;
            const IconComp = isHome ? LibraryBig : tab.icon;

            return (
              <div
                key={tab.id}
                title={displayTitle}
                draggable={!isHome}
                onDragStart={e => onTabDragStart(e, tab.id)}
                onDragOver={e => onTabDragOver(e, tab.id)}
                onDragLeave={onTabDragLeave}
                onDrop={e => onTabDrop(e, tab.id)}
                className={`flex items-center gap-2 text-sm cursor-pointer transition-all duration-300 ease-out whitespace-nowrap group min-w-[100px] md:min-w-0 md:flex-1 overflow-hidden px-3 md:px-4 py-2 ${activeTabId === tab.id
                  ? 'bg-background text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-slate-100/50'
                  } ${dragOverTabId === tab.id ? 'ring-2 ring-slate-300 ring-inset' : ''}`}
                onClick={() => onTabClick(tab.id)}
              >
                {IconComp && <IconComp className="w-4 h-4 shrink-0" />}
                <span className="min-w-0 truncate">{displayTitle}</span>
                {!isHome && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onTabClose(tab.id);
                    }}
                    className="ml-1 p-1 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-slate-200/50 transition-all duration-200 shrink-0"
                    aria-label={`Close ${displayTitle}`}
                  >
                    <IconX className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Fullscreen Toggle */}
        {onToggleFullscreen && (
          <div className="flex items-center px-2 bg-[#f0f0ef]">
            <button
              onClick={onToggleFullscreen}
              className="p-2 text-slate-600 hover:text-slate-900 transition-all duration-200 rounded-lg hover:bg-slate-100"
              title={isFullscreen ? "Show Sidebar" : "Hide Sidebar"}
            >
              {isFullscreen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5m5.5 11.5V19.5M9 15H4.5M9 15l-5.5 5.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M15 15h4.5M15 15v4.5m0-4.5l5.5 5.5" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

